import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { matchPath } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppIcon } from '@folio/stripes-core';
import { getLocalizedValue } from '../../helpers/localizationUtils';
import {
  Button,
  Icon,
  Loading,
  MultiColumnList,
  MCLPagingTypes,
  Pane,
  PaneMenu,
  Paneset,
  SearchField,
} from '@folio/stripes/components';
import {
  SearchAndSortQuery,
  SearchAndSortNoResultsMessage as NoResultsMessage,
  CollapseFilterPaneButton,
  ExpandFilterPaneButton,
} from '@folio/stripes/smart-components';
import EmptyData from '../common/emptyData/EmptyData';
import ScheduledReportsFilters from './ScheduledReportsFilters';
import ScheduledReportDetails from './ScheduledReportDetails';

function ScheduledReportsList({
  source,
  initialSearch,
  onNeedMoreData,
  queryGetter,
  querySetter,
  resources,
  mutator,
  history,
  location,
}) {
  const intl = useIntl();
  const { locale } = intl;
  const [filterPaneIsVisible, setFilterPaneIsVisible] = useState(true);

  // Extract scheduleId from URL — same pattern as ReportsList
  const urlMeta = matchPath(location?.pathname, {
    path: '/reports/scheduled-reports/:scheduleId/view',
  });
  const currentScheduleId = urlMeta?.params?.scheduleId || null;

  const scheduledReports = resources?.scheduledReports?.records || [];

  // Sync selectedSchedule from URL
  const selectedSchedule = currentScheduleId
    ? scheduledReports.find(r => r.id === currentScheduleId) || null
    : null;

  const handleRowClick = (e, item) => {
    history.push({
      pathname: `/reports/scheduled-reports/${item.id}/view`,
      search: location?.search,
    });
  };

  const handleCloseDetails = () => {
    history.push({
      pathname: '/reports/scheduled-reports',
      search: location?.search,
    });
  };
  const count = source ? source.totalCount() : 0;
  const query = queryGetter ? queryGetter() || {} : {};
  const sortOrder = query.sort || '';

  const reportsHasLoaded = resources?.scheduledReports?.hasLoaded || false;
  const activeFilterCount = query.filters
    ? query.filters.split(',').filter(Boolean).length
    : 0;

  // subtitle
  let paneSub;
  const isLoaded = reportsHasLoaded || scheduledReports.length > 0;
  const totalCount =
    source && source.totalCount() != null
      ? source.totalCount()
      : resources?.scheduledReports?.other?.totalRecords ||
        scheduledReports.length;

  if (!isLoaded) {
    paneSub = <FormattedMessage id="stripes-smart-components.searchCriteria" />;
  } else if (query.query || activeFilterCount > 0) {
    const searchDesc = query.query
      ? intl.formatMessage(
          { id: 'ui-reports.search.resultsForTerm' },
          { term: query.query },
        )
      : null;
    const filterDesc =
      activeFilterCount > 0
        ? intl.formatMessage(
            { id: 'ui-reports.search.activeFiltersCount' },
            { count: activeFilterCount },
          )
        : null;
    const parts = [searchDesc, filterDesc].filter(Boolean).join(' — ');
    paneSub = (
      <>
        <FormattedMessage
          id="stripes-smart-components.searchResultsCountHeader"
          values={{ count: totalCount }}
        />
        {parts && (
          <span
            style={{
              marginInlineStart: '0.5em',
              color: 'var(--color-text-secondary,#666)',
            }}
          >
            ({parts})
          </span>
        )}
      </>
    );
  } else {
    paneSub = (
      <FormattedMessage
        id="stripes-smart-components.searchResultsCountHeader"
        values={{ count: totalCount }}
      />
    );
  }

  const columnMapping = {
    name:              <FormattedMessage id="ui-reports.scheduledReports.name" />,
    frequency:         <FormattedMessage id="ui-reports.scheduledReports.frequency" />,
    outputFormat:      <FormattedMessage id="ui-reports.scheduledReports.outputFormat" />,
    nextExecutionDate: <FormattedMessage id="ui-reports.scheduledReports.nextExecution" />,
    isActive:          <FormattedMessage id="ui-reports.scheduledReports.status" />,
  };

  const formatter = {
    name: (item) => getLocalizedValue(item.name, locale) || '—',
    frequency: (item) => (
      <FormattedMessage
        id={`ui-reports.scheduledReports.frequency.${item.frequency}`}
        defaultMessage={item.frequency || '—'}
      />
    ),
    nextExecutionDate: (item) =>
      item.nextExecutionDate
        ? new Date(item.nextExecutionDate).toLocaleString()
        : '-',
    isActive: (item) =>
      item.isActive ? (
        <FormattedMessage id="ui-reports.scheduledReports.active" />
      ) : (
        <FormattedMessage id="ui-reports.scheduledReports.paused" />
      ),
  };

  const resultsStatusMessage = source ? (
    <NoResultsMessage
      source={source}
      searchTerm={query.query || ''}
      filterPaneIsVisible
      toggleFilterPane={noop}
    />
  ) : (
    <EmptyData />
  );

  return (
    <SearchAndSortQuery
      querySetter={querySetter}
      queryGetter={queryGetter}
      initialSearch={initialSearch}
      initialSearchState={{ query: '' }}
    >
      {({
        searchValue,
        getSearchHandlers,
        onSubmitSearch,
        onSort,
        getFilterHandlers,
        activeFilters,
        filterChanged,
        searchChanged,
        resetAll,
      }) => (
        <Paneset isRoot>
          {/* Filter Pane */}
          {filterPaneIsVisible && (
            <Pane
              defaultWidth="22%"
              paneTitle={
                <FormattedMessage id="ui-reports.panes.filtersPane.title" />
              }
              lastMenu={
                <PaneMenu>
                  <CollapseFilterPaneButton
                    onClick={() => setFilterPaneIsVisible(false)}
                  />
                </PaneMenu>
              }
            >
              <form onSubmit={onSubmitSearch}>
                <SearchField
                  aria-label={intl.formatMessage({
                    id: 'ui-reports.panes.scheduledReports.title',
                  })}
                  autoFocus
                  autoComplete="off"
                  name="query"
                  id="input-scheduled-reports-search"
                  onChange={(e) => {
                    if (e.target.value) getSearchHandlers().query(e);
                    else getSearchHandlers().reset();
                  }}
                  value={searchValue.query}
                  marginBottom0
                />
                <div style={{ marginBottom: '0.5rem' }} />
                <Button
                  id="submit-scheduled-reports-search"
                  type="submit"
                  buttonStyle="primary"
                  fullWidth
                  marginBottom0
                  disabled={!searchValue.query}
                >
                  <FormattedMessage id="stripes-smart-components.search" />
                </Button>
                <div style={{ marginBottom: '0.5rem' }} />
                <Button
                  buttonStyle="none"
                  id="clickable-reset-all-scheduled-reports"
                  disabled={!(filterChanged || searchChanged)}
                  fullWidth
                  onClick={resetAll}
                >
                  <Icon icon="times-circle-solid">
                    <FormattedMessage id="stripes-smart-components.resetAll" />
                  </Icon>
                </Button>
                <div style={{ marginBottom: '1rem' }} />
                <ScheduledReportsFilters
                  activeFilters={activeFilters.state}
                  onChangeHandlers={getFilterHandlers()}
                  resultOffset={mutator.resultOffset}
                />
              </form>
            </Pane>
          )}

          {/* Results Pane */}
          <Pane
            id="pane-scheduled-reports-list"
            appIcon={<AppIcon app="reports" />}
            defaultWidth="fill"
            padContent={false}
            noOverflow
            paneTitle={
              <FormattedMessage id="ui-reports.panes.scheduledReports.title" />
            }
            paneSub={paneSub}
            firstMenu={
              !filterPaneIsVisible ? (
                <PaneMenu>
                  <ExpandFilterPaneButton
                    filterCount={activeFilterCount}
                    onClick={() => setFilterPaneIsVisible(true)}
                  />
                </PaneMenu>
              ) : null
            }
          >
            {!isLoaded ? (
              <Loading />
            ) : (
              <MultiColumnList
                id="scheduled-reports-list"
                interactive
                visibleColumns={[
                  'name',
                  'frequency',
                  'outputFormat',
                  'nextExecutionDate',
                  'isActive',
                ]}
                columnWidths={{
                  name:              '30%',
                  frequency:         '16%',
                  outputFormat:      '13%',
                  nextExecutionDate: '27%',
                  isActive:          '14%',
                }}
                contentData={scheduledReports}
                totalCount={totalCount}
                columnMapping={columnMapping}
                formatter={formatter}
                onNeedMoreData={onNeedMoreData}
                onHeaderClick={onSort}
                sortOrder={sortOrder.replace(/^-/, '').replace(/,.*/, '')}
                sortDirection={
                  sortOrder.startsWith('-') ? 'descending' : 'ascending'
                }
                isEmptyMessage={resultsStatusMessage}
                isSelected={({ item }) => item.id === selectedSchedule?.id}
                onRowClick={handleRowClick}
                autosize
                hasMargin
                pageAmount={30}
                pagingType={MCLPagingTypes.PREV_NEXT}
              />
            )}
          </Pane>

          {/* ── Details Pane ── */}
          {selectedSchedule && (
            <ScheduledReportDetails
              schedule={selectedSchedule}
              onClose={handleCloseDetails}
              onDeleted={() => handleCloseDetails()}
              onDeleteSchedule={(id) => {
                mutator.activeRecord.update({ id });
                return mutator.scheduledReports.DELETE({ id });
              }}
              onRefresh={() => {
                if (mutator?.resultOffset) mutator.resultOffset.replace(0);
                if (source) source.fetchMore(0);
              }}
            />
          )}

        </Paneset>
      )}
    </SearchAndSortQuery>
  );
}

ScheduledReportsList.propTypes = {
  source:         PropTypes.object,
  initialSearch:  PropTypes.string,
  onNeedMoreData: PropTypes.func.isRequired,
  queryGetter:    PropTypes.func,
  querySetter:    PropTypes.func,
  history:        PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  location:       PropTypes.object.isRequired,
  resources:      PropTypes.shape({ scheduledReports: PropTypes.object }).isRequired,
  mutator: PropTypes.shape({
    resultOffset:      PropTypes.shape({ replace: PropTypes.func.isRequired }),
    activeRecord:      PropTypes.shape({ update: PropTypes.func.isRequired }),
    scheduledReports:  PropTypes.shape({ DELETE: PropTypes.func.isRequired }),
  }).isRequired,
};

export default ScheduledReportsList;
